import { Skeleton } from 'antd';
import Highcharts, { Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { Switcher } from 'components/atoms/switcher';
import { TitleWithTooltip } from 'components/organisms/projects-report/widgets/components';
import { ProductiveHeading07 } from 'components/styles';

type Tab<T extends string> = {
  key: T;
  label: string;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--white-100);
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: var(--spacing-05);
  align-items: center;
`;
const Header = styled.h1`
  ${ProductiveHeading07};
  font-weight: var(--font-weight-bold) !important;
`;

const SwitcherWrapper = styled.div`
  > div > * {
    z-index: 10;
  }
`;

const EmptyWrapper = styled.div`
  height: 280px;
  width: 100%;
  position: relative;
  div {
    position: absolute;
    bottom: 41px;
    left: 32px;
  }
`;

interface Props<T extends string> {
  chartOption: Options;
  immutable?: boolean;
  title: string;
  tooltip?: ReactNode;
  tabs?: Array<Tab<T>>;
  activeTab?: string;
  onTabSwitch?: (value: T) => void;
  renderEmpty: () => ReactNode;
  isLoading: boolean;
}

export const TemplateInsightBaseChart = <T extends string>({
  chartOption,
  immutable = false,
  title,
  tooltip = null,
  tabs,
  activeTab,
  onTabSwitch,
  renderEmpty,
  isLoading,
}: Props<T>): JSX.Element => {
  const isEmpty =
    chartOption?.series?.[0] == null ||
    ((chartOption?.series?.[0]?.type === 'line' ||
      chartOption?.series?.[0]?.type === 'column') &&
      chartOption.series?.[0].data?.length === 0);

  if (isLoading) {
    return <Skeleton paragraph={{ rows: 6 }} active />;
  }

  return (
    <Wrapper>
      <HeaderWrapper>
        <TitleWithTooltip title={<Header>{title}</Header>} tooltip={tooltip} />
        {tabs && (
          <SwitcherWrapper>
            <Switcher
              value={activeTab}
              onChange={(e) => onTabSwitch?.(e.target.value)}
              activeColor="black"
            >
              {tabs.map(({ key, label }) => (
                <Switcher.Button key={key} value={key}>
                  {label}
                </Switcher.Button>
              ))}
            </Switcher>
          </SwitcherWrapper>
        )}
      </HeaderWrapper>
      {!isLoading && isEmpty ? (
        <EmptyWrapper>{renderEmpty()}</EmptyWrapper>
      ) : (
        <HighchartsReact
          highcharts={Highcharts}
          options={{
            ...chartOption,
            title: '',
            chart: {
              spacingBottom: 16,
              spacingLeft: 16,
              spacingRight: 16,
              ...chartOption.chart,
              height: 280,
            },
          }}
          immutable={immutable}
        />
      )}
    </Wrapper>
  );
};
